/**
 * CandidateMatchResults Component
 * 
 * Displays matching analysis scores for candidates on their own applications.
 * Shows the same scores that companies see - no adjustment.
 * 
 * Features:
 * - Progressive loading with animated messages
 * - On-mount trigger (auto-fetches when tab is opened)
 * - 202 polling for cache misses
 * - Works for all application statuses including rejected
 * - Shows all 8 dimensions matching company view
 * - Pre-screen results and recommendations
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Target, 
  Users, 
  Briefcase,
  CheckCircle,
  CheckCircle2,
  AlertCircle,
  Lightbulb,
  TrendingUp,
  GraduationCap,
  MapPin,
  Trophy,
  UserCheck,
  XCircle,
  AlertTriangle,
  Info,
  Zap,
  ArrowRight,
  type LucideIcon
} from 'lucide-react';
import { SkillsCoverageBar, type TransferableSkill } from "@/components/matching/SkillsMatchAnalysisPanel";
import { DomainAlignmentBar } from "@/components/matching/DomainAlignmentBar";

interface CandidateMatchResultsProps {
  applicationId: string;
}

interface PreScreenFilter {
  rule: string;
  passed: boolean;
  reason: string;
  evidence?: string[];
}

interface Recommendation {
  target: string;
  action: string;
  estimatedEffort?: string;
  expectedImpact?: string;
  resources?: string[];
}

interface SkillItem {
  skill: string;
  candidateProficiency?: string;
  jobRequirement?: string;
  evidenceSource?: string;
  matchQuality?: number;
  importance?: string;
  alternatives?: string[];
  learnability?: string;
  isDirect?: boolean;
  viaSkill?: string;
  // Task #137: final related-match score (edge weight × type multiplier).
  // Only set for related matches that cleared the
  // skill.related_match_promotion_threshold cutoff.
  relatedScore?: number;
}

interface SkillsDimensionBreakdown {
  score?: number;
  matched?: SkillItem[];
  missing?: SkillItem[];
  directMatchCount?: number;
  relatedMatchCount?: number;
  transferable?: Array<{ fromSkill: string; toSkill: string }>;
  softSkillsMatched?: Array<{ skill: string }>;
}

interface CandidateSummary {
  summary: string;
  topStrengths: string[];
  keyGaps: string[];
  actionableAdvice: string[];
  cached: boolean;
}

interface RecapData {
  pros?: string[];
  cons?: string[];
  summary?: string;
}

interface DimensionBreakdown {
  score?: number;
  [key: string]: unknown;
}

interface MatchResultsAnalysis {
  overallScore: number;
  skills: number;
  experience: number;
  culture: number;
  breakdown: {
    skillScore: number;
    experienceScore: number;
    culturalScore: number;
    compatibilityScore: number;
    vectorSimilarityScore?: number;
  };
  scores: Record<string, number>;
  dimensionBreakdowns: Record<string, SkillsDimensionBreakdown | DimensionBreakdown | undefined>;
  preFilterInclusion: PreScreenFilter[] | null;
  executiveSummary: string | null;
  gapAnalysis: Array<{ category?: string; gap?: string; impact?: string }> | null;
  recommendations: Recommendation[] | null;
  areasForConsideration: Array<{ area: string; details: string }> | null;
  recap: RecapData | string | null;
  candidateSummary?: CandidateSummary;
}

interface MatchResultsResponse {
  status: 'complete' | 'generating' | 'queued';
  generatedAt?: string | null;
  estimatedSeconds?: number;
  analysis?: MatchResultsAnalysis;
}

const scoreConfig: Record<string, { icon: LucideIcon; color: string; label: string }> = {
  'Skills Match (50%)': { icon: Target, color: 'text-blue-600', label: 'Skills' },
  'Seniority (10%)': { icon: UserCheck, color: 'text-indigo-600', label: 'Seniority' },
  'Experience (10%)': { icon: TrendingUp, color: 'text-green-600', label: 'Experience' },
  'Title Match (5%)': { icon: Briefcase, color: 'text-cyan-600', label: 'Title' },
  'Education (5%)': { icon: GraduationCap, color: 'text-purple-600', label: 'Education' },
  'Location (10%)': { icon: MapPin, color: 'text-orange-600', label: 'Location' },
  'Cultural Fit (5%)': { icon: Users, color: 'text-pink-600', label: 'Culture' },
  'Achievements (5%)': { icon: Trophy, color: 'text-yellow-600', label: 'Achievements' },
};

export function getScoreLevel(score: number): { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; bgColor: string; textColor: string } {
  if (score >= 70) return { label: 'Strong', variant: 'default', bgColor: 'bg-green-500', textColor: 'text-green-600' };
  if (score >= 50) return { label: 'Good', variant: 'secondary', bgColor: 'bg-amber-500', textColor: 'text-amber-600' };
  return { label: 'Needs Work', variant: 'destructive', bgColor: 'bg-red-500', textColor: 'text-red-600' };
}

export function normalizeScore(value: number): number {
  if (value > 0 && value <= 1) {
    return Math.round(value * 100);
  }
  return Math.round(value);
}

export { scoreConfig };

function ScoreBreakdownCards({ scores }: { scores: Record<string, number> }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {Object.entries(scores).map(([key, rawValue]) => {
        const value = normalizeScore(rawValue);
        const config = scoreConfig[key] || { icon: Target, color: 'text-gray-600', label: key.replace(/[()%0-9]/g, '').trim() };
        const Icon = config.icon;
        const level = getScoreLevel(value);
        
        return (
          <TooltipProvider key={key}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Card className="relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow" data-testid={`score-card-${config.label.toLowerCase()}`}>
                  <div className={`absolute top-0 left-0 w-1 h-full ${level.bgColor}`} />
                  <CardContent className="p-3 pl-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={`h-4 w-4 ${config.color}`} />
                      <span className="text-xs font-medium text-muted-foreground truncate">
                        {config.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xl font-bold">{Math.round(value)}%</span>
                      <Badge variant={level.variant} className="text-xs">
                        {level.label}
                      </Badge>
                    </div>
                    <Progress value={value} className="h-1.5" />
                  </CardContent>
                </Card>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-sm">{config.label}: {Math.round(value)}%</p>
                <p className="text-xs text-muted-foreground">
                  {value >= 80 ? 'Excellent match for this dimension' :
                   value >= 60 ? 'Good alignment, minor gaps possible' :
                   value >= 40 ? 'Moderate fit, room for growth' :
                   'Area to focus on for similar roles'}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
}

function PreScreenResultsCards({ filters }: { filters: PreScreenFilter[] }) {
  if (!filters || filters.length === 0) return null;

  const passedCount = filters.filter(f => f.passed && f.rule !== 'job_quality').length;
  const totalCount = filters.filter(f => f.rule !== 'job_quality').length;
  const progressValue = totalCount > 0 ? (passedCount / totalCount) * 100 : 0;

  return (
    <Card data-testid="pre-screen-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CheckCircle className="h-4 w-4 text-emerald-500" />
          Pre-Screen Results
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-green-100 dark:bg-green-900 rounded">
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            </div>
            <span className="text-sm font-medium">{passedCount}/{totalCount} criteria met</span>
          </div>
          <Progress value={progressValue} className="h-2 flex-1 max-w-[200px]" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filters.map((filter, idx) => {
            const isQualityPenalty = filter.rule === 'job_quality';
            const isPassed = filter.passed && !isQualityPenalty;
            
            return (
              <Card 
                key={idx} 
                className={`border-l-4 ${
                  isQualityPenalty ? 'border-l-orange-500' :
                  isPassed ? 'border-l-green-500' : 'border-l-amber-500'
                }`}
                data-testid={`pre-screen-filter-${idx}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-start gap-2">
                      {isQualityPenalty ? (
                        <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
                      ) : isPassed ? (
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                      )}
                      <span className="font-medium text-sm capitalize">
                        {filter.rule.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <Badge 
                      variant={isQualityPenalty ? 'destructive' : isPassed ? 'default' : 'secondary'}
                      className="text-xs shrink-0"
                    >
                      {isQualityPenalty ? 'Note' : isPassed ? 'Met' : 'Not Met'}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground ml-6">{filter.reason}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function CandidateSummaryCard({ summary, missingSkills }: { summary: CandidateSummary; missingSkills?: SkillItem[] }) {
  if (!summary?.summary) return null;

  const quickSkill = missingSkills?.find(s => s.learnability === 'quick');

  return (
    <Card className="border-l-4 border-l-primary bg-gradient-to-r from-primary/5 to-transparent" data-testid="candidate-summary-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Lightbulb className="h-4 w-4 text-primary" />
          Your Personalized Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm leading-relaxed text-foreground">
          {summary.summary}
        </p>
        
        {summary.topStrengths && summary.topStrengths.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Your Strengths</h4>
            <div className="flex flex-wrap gap-2">
              {summary.topStrengths.map((strength, i) => (
                <Badge key={i} variant="secondary" className="bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {strength}
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        {summary.keyGaps && summary.keyGaps.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Areas to Develop</h4>
            <div className="flex flex-wrap gap-2">
              {summary.keyGaps.map((gap, i) => (
                <Badge key={i} variant="outline" className="border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  {gap}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {quickSkill && (
          <div className="bg-primary/5 rounded-md p-3 border border-primary/20 dark:bg-primary/10 dark:border-primary/30">
            <Zap className="h-3.5 w-3.5 text-primary inline mr-1" />
            <span className="text-sm">
              Adding {quickSkill.skill} could make you a significantly stronger match
              for this and similar roles.
            </span>
          </div>
        )}
        
        {summary.actionableAdvice && summary.actionableAdvice.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Next Steps</h4>
            <ul className="space-y-1.5">
              {summary.actionableAdvice.map((advice, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Zap className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  {advice}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function CandidateMatchResults({ applicationId }: CandidateMatchResultsProps) {
  const [loadingMessage, setLoadingMessage] = useState('Loading your match analysis...');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [hasStartedLoading, setHasStartedLoading] = useState(false);
  const [shouldPoll, setShouldPoll] = useState(false);
  
  const { data, isLoading, error } = useQuery<MatchResultsResponse>({
    queryKey: ['/api/candidates/applications', applicationId, 'match-results'],
    queryFn: async () => {
      const response = await fetch(`/api/candidates/applications/${applicationId}/match-results`, {
        credentials: 'include',
      });
      
      if (response.status === 202) {
        const responseData = await response.json();
        
        setLoadingMessage(
          responseData.status === 'queued' 
            ? 'Analysis queued for generation...' 
            : 'Generating your match analysis...'
        );
        
        setShouldPoll(true);
        
        return {
          status: responseData.status as 'generating' | 'queued',
          estimatedSeconds: responseData.estimatedSeconds,
        };
      }
      
      setShouldPoll(false);
      
      if (!response.ok) {
        throw new Error('Failed to fetch match results');
      }
      
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: shouldPoll ? 2000 : false,
    refetchIntervalInBackground: false,
  });
  
  useEffect(() => {
    if (data?.status === 'complete') {
      setHasStartedLoading(false);
      setLoadingProgress(0);
      return;
    }
    
    if (!hasStartedLoading && (isLoading || data?.status === 'generating' || data?.status === 'queued')) {
      setHasStartedLoading(true);
      setLoadingProgress(10);
    }
    
    if (!hasStartedLoading) return;
    
    const messages = [
      { time: 500, text: 'Checking for cached analysis...', progress: 15 },
      { time: 2000, text: 'Analyzing your profile match...', progress: 30 },
      { time: 4000, text: 'Comparing skills and experience...', progress: 50 },
      { time: 6000, text: 'Evaluating cultural alignment...', progress: 65 },
      { time: 8000, text: 'Generating insights...', progress: 80 },
      { time: 12000, text: 'Almost there...', progress: 90 },
    ];
    
    const timers = messages.map(({ time, text, progress }) => 
      setTimeout(() => {
        if (data?.status !== 'complete') {
          setLoadingMessage(text);
          setLoadingProgress(progress);
        }
      }, time)
    );
    
    return () => timers.forEach(t => clearTimeout(t));
  }, [isLoading, hasStartedLoading, data?.status]);
  
  if (error) {
    return (
      <Card className="border-destructive" data-testid="match-results-error">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <span>Unable to load match results. Please try again later.</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (isLoading || data?.status === 'generating' || data?.status === 'queued') {
    return (
      <Card data-testid="match-results-loading">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary animate-pulse" />
            Analyzing Your Match
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{loadingMessage}</span>
              <span className="text-muted-foreground">{loadingProgress}%</span>
            </div>
            <Progress value={loadingProgress} className="h-2" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
          <p className="text-sm text-muted-foreground text-center mt-4">
            This typically takes 5-10 seconds for first-time analysis
          </p>
        </CardContent>
      </Card>
    );
  }
  
  if (!data?.analysis) {
    return (
      <Card data-testid="match-results-unavailable">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 text-muted-foreground">
            <AlertCircle className="h-5 w-5" />
            <span>Match analysis is not available for this application.</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const analysis = data.analysis;
  const dimensionBreakdowns = analysis.dimensionBreakdowns || {};
  
  const skillsData = dimensionBreakdowns?.skills as SkillsDimensionBreakdown | undefined;
  const matchedSkills = Array.isArray(skillsData?.matched) ? skillsData.matched : [];
  const missingSkills = Array.isArray(skillsData?.missing) ? skillsData.missing : [];
  
  const preFilterInclusion = Array.isArray(analysis.preFilterInclusion) ? analysis.preFilterInclusion : [];
  const gapAnalysis = Array.isArray(analysis.gapAnalysis) ? analysis.gapAnalysis : [];
  
  return (
    <div className="space-y-6" data-testid="match-results-complete">
      {/* Overall Score Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              Your Match Score
            </CardTitle>
            <Badge 
              variant={analysis.overallScore >= 80 ? "default" : analysis.overallScore >= 60 ? "secondary" : "outline"}
              className="text-lg px-3 py-1"
              data-testid="overall-score-badge"
            >
              {analysis.overallScore.toFixed(0)}%
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Progress value={analysis.overallScore} className="h-3" />
          <div className="text-sm text-muted-foreground">
            {analysis.overallScore >= 80 
              ? "You're a strong match for this position!" 
              : analysis.overallScore >= 60 
                ? "You have a good foundation for this role."
                : "There are some areas where you could strengthen your profile."}
          </div>
          <DomainAlignmentBar score={analysis.breakdown?.vectorSimilarityScore} />
        </CardContent>
      </Card>
      
      {/* AI-Generated Candidate Summary (personalized second-person summary) */}
      {analysis.candidateSummary && (
        <CandidateSummaryCard summary={analysis.candidateSummary} missingSkills={missingSkills} />
      )}
      
      {/* All Dimension Scores - 8 cards in grid */}
      {analysis.scores && Object.keys(analysis.scores).length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-3">Match Breakdown by Dimension</h3>
          <ScoreBreakdownCards scores={analysis.scores} />
        </div>
      )}
      
      {/* Pre-Screen Results */}
      {preFilterInclusion.length > 0 && (
        <PreScreenResultsCards filters={preFilterInclusion} />
      )}
      
      {/* Matched Skills */}
      {matchedSkills.length > 0 && (
        <Card data-testid="matched-skills-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle className="h-4 w-4 text-emerald-500" />
              Your Matching Skills
            </CardTitle>
            <div className="mt-1">
              <SkillsCoverageBar
                directMatchCount={skillsData?.directMatchCount}
                relatedMatchCount={skillsData?.relatedMatchCount}
                matchedCount={matchedSkills.length}
                missingCount={missingSkills.length}
                height="h-1.5"
              />
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              const transferableMap = new Map<string, string>();
              const transferable = skillsData?.transferable as TransferableSkill[] | undefined;
              if (transferable) {
                for (const t of transferable) {
                  transferableMap.set(t.toSkill.toLowerCase(), t.fromSkill);
                }
              }
              const directItems: SkillItem[] = [];
              const relatedItems: Array<SkillItem & { viaSkill?: string; relatedScore?: number }> = [];
              for (const item of matchedSkills.slice(0, 15)) {
                const typedItem = item as SkillItem & { isDirect?: boolean; viaSkill?: string; relatedScore?: number };
                const serverViaSkill = typedItem.viaSkill;
                const transferableVia = transferableMap.get(item.skill.toLowerCase());
                const resolvedViaSkill = serverViaSkill || transferableVia;
                const isRelated = typedItem.isDirect === false || (
                  typedItem.isDirect === undefined && resolvedViaSkill !== undefined
                );
                if (isRelated) relatedItems.push({ ...item, viaSkill: resolvedViaSkill, relatedScore: typedItem.relatedScore });
                else directItems.push(item);
              }
              const hasSplit = directItems.length > 0 && relatedItems.length > 0;

              return (
                <div className="space-y-3">
                  {hasSplit ? (
                    <>
                      {directItems.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                            Direct matches ({directItems.length})
                          </p>
                          <div className="space-y-1 pl-3">
                            {directItems.map((item, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                                <span className="text-sm">{item.skill}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {relatedItems.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                            <span className="inline-block w-2 h-2 rounded-full bg-blue-400" />
                            Related matches ({relatedItems.length})
                          </p>
                          <div className="space-y-1.5 pl-3">
                            {relatedItems.map((item, i) => (
                              <div key={i} className="flex items-center gap-2 flex-wrap">
                                <CheckCircle2 className="h-3.5 w-3.5 text-blue-400 shrink-0" />
                                <span className="text-sm">≈ {item.skill}</span>
                                {item.viaSkill && (
                                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <ArrowRight className="h-3 w-3" />
                                    you have <span className="font-medium text-foreground">{item.viaSkill}</span>
                                  </span>
                                )}
                                {typeof item.relatedScore === 'number' && (
                                  <span
                                    className="text-xs text-muted-foreground"
                                    data-testid={`related-score-${item.skill}`}
                                  >
                                    (related, {item.relatedScore.toFixed(2)})
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="space-y-1">
                      {matchedSkills.slice(0, 15).map((item, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                          <span className="text-sm">{item.skill}</span>
                        </div>
                      ))}
                      {skillsData?.relatedMatchCount !== undefined && skillsData.relatedMatchCount > 0 && (
                        <p className="text-xs text-muted-foreground pt-1">
                          <span className="inline-block w-2 h-2 rounded-full bg-blue-400 mr-1" />
                          {skillsData.relatedMatchCount} of these matched via related skills in our taxonomy.
                        </p>
                      )}
                    </div>
                  )}
                  {matchedSkills.length > 15 && (
                    <p className="text-xs text-muted-foreground">+{matchedSkills.length - 15} more skills matched</p>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}
      
      {/* Skills to Develop */}
      {missingSkills.length > 0 && (
        <Card data-testid="skills-gap-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-4 w-4 text-amber-500" />
              Skills to Develop
              {missingSkills.some(s => s.learnability === 'extensive') && (
                <span className="text-sm font-normal text-muted-foreground">(some require significant investment)</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Developing these skills could strengthen your profile for similar roles:
            </p>
            <div className="space-y-2">
              {[...missingSkills]
                .sort((a, b) => {
                  const order: Record<string, number> = { critical: 0, important: 1, beneficial: 2 };
                  return (order[a.importance ?? ''] ?? 2) - (order[b.importance ?? ''] ?? 2);
                })
                .slice(0, 10)
                .map((s, i) => {
                const learnabilityLabel = s.learnability === 'quick'
                  ? 'Quick to learn'
                  : s.learnability === 'extensive'
                    ? 'Extensive investment'
                    : 'Moderate to learn';
                const learnabilityColor = s.learnability === 'quick'
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : s.learnability === 'extensive'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-amber-600 dark:text-amber-400';
                const importanceIcon = s.importance === 'critical'
                  ? <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
                  : s.importance === 'important'
                    ? <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
                    : <Info className="h-3.5 w-3.5 text-muted-foreground shrink-0" />;
                return (
                  <div key={i} className="space-y-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      {importanceIcon}
                      <span className="bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 text-xs rounded-md px-2 py-0.5">
                        {s.skill}
                      </span>
                      {s.learnability && (
                        <span className={`text-xs ${learnabilityColor}`}>
                          · {learnabilityLabel}
                        </span>
                      )}
                    </div>
                    {s.alternatives && s.alternatives.length > 0 && (
                      <p className="text-xs text-muted-foreground ml-5">
                        You already have {s.alternatives.join(', ')}, which is related — a head start.
                      </p>
                    )}
                  </div>
                );
              })}
              {missingSkills.length > 10 && (
                <p className="text-xs text-muted-foreground">+{missingSkills.length - 10} more skills</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transferable Skills — renders when backend provides data (P2-C) */}
      {skillsData?.transferable && skillsData.transferable.length > 0 && (
        <Card data-testid="transferable-skills-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle className="h-4 w-4 text-blue-500" />
              Skills That Count Toward This Role
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {skillsData.transferable.map((t, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <Badge className="bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                  {t.fromSkill}
                </Badge>
                <span className="text-xs text-muted-foreground">relates to {t.toSkill}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      
      {/* Gap Analysis */}
      {gapAnalysis.length > 0 && (
        <Card data-testid="gap-analysis-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Areas for Consideration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {gapAnalysis.slice(0, 4).map((gap, i) => (
                <li key={i} className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{gap.category || gap.gap}:</span>{' '}
                  {gap.impact || gap.gap}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
      
      {/* Generated Timestamp */}
      {data.generatedAt && (
        <p className="text-xs text-muted-foreground text-center" data-testid="generated-at">
          Analysis generated on {new Date(data.generatedAt).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          })}
        </p>
      )}
    </div>
  );
}
